import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Typography,
  Box,
  Chip,
  Avatar,
  IconButton,
  Divider,
  CircularProgress,
  Tabs,
  Tab,
  FormControlLabel,
  Checkbox,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Paper,
  Autocomplete,
} from '@mui/material';
import {
  Close,
  Save,
  Edit,
  Email,
  Phone,
  LocationOn,
  CalendarToday,
  Notes,
  Add,
  Delete,
} from '@mui/icons-material';
import toast from 'react-hot-toast';
import { useQuery } from '@tanstack/react-query';
import CallLogPanel from '../Cases/CallLogPanel';
import {
  toDateOnly,
  todayDateInputValue,
  toDatetimeLocalValue,
  fromDatetimeLocalValue,
} from '../../utils/datetimeLocal';
import { getAuthToken } from '../../lib/authToken';
import { useAuth } from '../../contexts/AuthContext';
import {
  ASSIGNABLE_USERS_QUERY_KEY,
  asAssignableUsers,
  fetchAssignableUsers,
} from '../../lib/assignableUsers';
import CLIENT_LANGUAGES from '../../constants/languages';

/** Roles the backend accepts as the intake interviewer (clients route parity). */
const INTAKE_STAFF_ROLES = ['admin', 'lawyer', 'paralegal', 'assistant'];

export interface ClientContact {
  id?: string;
  contactType: string;
  firstName?: string;
  lastName?: string;
  organizationName?: string;
  relationship?: string;
  email?: string;
  phone?: string;
  address?: string;
  dateOfBirth?: string | null;
  isDependent?: boolean;
  livesWithClient?: boolean;
  authorizedToDiscuss?: boolean;
  isPrimary?: boolean;
  roleTitle?: string;
  ownershipPercent?: number | null;
  notes?: string;
}

interface Client {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  address: string;
  mailingAddress?: string;
  city?: string;
  state?: string;
  county?: string;
  zipCode?: string;
  preferredName?: string;
  dateOfBirth: string | null;
  clientType: 'individual' | 'corporation' | 'partnership' | 'llc' | 'other';
  status: 'active' | 'inactive' | 'prospect';
  maritalStatus?: string;
  spouseName?: string;
  /** Virtual — synced to Related People spouse contact on save */
  spousePhone?: string;
  spouseEmail?: string;
  employmentStatus?: string;
  employerName?: string;
  /** Stored as jobTitle; UI label is Occupation for template parity */
  jobTitle?: string;
  preferredContactMethod?: string;
  preferredLanguage?: string;
  interpreterRequired?: boolean;
  safeToLeaveVoicemail?: boolean | null;
  entityLegalName?: string;
  entityDba?: string;
  entityJurisdiction?: string;
  entityFormationDate?: string | null;
  businessPurpose?: string;
  referralSource?: string;
  referralDetails?: string;
  /** Timestamp of the intake interview; auto-stamped on create, editable after. */
  intakeAt?: string | null;
  intakeByUserId?: string | null;
  intakeBy?: {
    id: string;
    firstName?: string;
    lastName?: string;
    email?: string;
    role?: string;
  } | null;
  intakeProfile?: Record<string, unknown>;
  notes: string;
  metadata: any;
  contacts?: ClientContact[];
  createdAt: string;
  updatedAt: string;
}

interface ClientDialogProps {
  open: boolean;
  onClose: () => void;
  clientData?: Client | null;
  mode: 'view' | 'edit' | 'create';
  onSave: (clientData: Partial<Client>) => Promise<void>;
  onModeChange?: (mode: 'view' | 'edit' | 'create') => void;
}

const emptyContact = (): ClientContact => ({
  contactType: 'next_of_kin',
  firstName: '',
  lastName: '',
  relationship: '',
  phone: '',
  email: '',
  authorizedToDiscuss: false,
  isPrimary: false,
  isDependent: false,
  livesWithClient: false,
});

const CONTACT_TYPE_LABELS: Record<string, string> = {
  next_of_kin: 'Next of kin',
  emergency: 'Emergency contact',
  family: 'Family / household',
  spouse: 'Spouse / partner',
  beneficiary: 'Beneficiary (specific)',
  residual_beneficiary: 'Residual beneficiary',
  executor: 'Executor (will)',
  alternate_executor: 'Alternate executor',
  trustee: 'Trustee',
  successor_trustee: 'Successor trustee',
  guardian: 'Guardian (minors)',
  primary_agent: 'Primary agent (POA)',
  successor_agent: 'Successor agent (POA)',
  witness: 'Witness',
  officer: 'Officer / director',
  owner: 'Owner',
  authorized_signatory: 'Authorized signatory',
  authorized_contact: 'Authorized to discuss',
  other: 'Other',
};

const ClientDialog: React.FC<ClientDialogProps> = ({
  open,
  onClose,
  clientData,
  mode,
  onSave,
  onModeChange,
}) => {
  const [tab, setTab] = useState(0);
  const [formData, setFormData] = useState<Partial<Client>>({});
  const [contacts, setContacts] = useState<ClientContact[]>([]);
  const [draftContact, setDraftContact] = useState<ClientContact>(emptyContact());
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [contactsLoading, setContactsLoading] = useState(false);
  const { user: currentUser } = useAuth();
  const currentUserIsIntakeStaff = INTAKE_STAFF_ROLES.includes(String(currentUser?.role || ''));

  const { data: intakeStaffData } = useQuery({
    queryKey: ASSIGNABLE_USERS_QUERY_KEY,
    enabled: open,
    queryFn: fetchAssignableUsers,
  });
  const intakeStaff = asAssignableUsers(intakeStaffData);

  const isBusiness = ['corporation', 'partnership', 'llc', 'other'].includes(
    formData.clientType || clientData?.clientType || 'individual'
  );
  const readOnly = mode === 'view';

  const loadContacts = useCallback(async (clientId: string) => {
    setContactsLoading(true);
    try {
      const token = getAuthToken();
      const res = await fetch(`/api/clients/${clientId}/contacts`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!res.ok) throw new Error('Failed to load contacts');
      const json = await res.json();
      setContacts(json.data?.contacts || []);
    } catch {
      setContacts([]);
    } finally {
      setContactsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!open) return;
    setTab(0);
    if (clientData && mode !== 'create') {
      const intakeProfile = (clientData.intakeProfile || {}) as Record<string, unknown>;
      const spouseFromContacts = (clientData.contacts || []).find((c) => c.contactType === 'spouse');
      setFormData({
        ...clientData,
        dateOfBirth: clientData.dateOfBirth ? String(clientData.dateOfBirth).split('T')[0] : null,
        entityFormationDate: clientData.entityFormationDate
          ? String(clientData.entityFormationDate).split('T')[0]
          : null,
        spousePhone:
          clientData.spousePhone
          || spouseFromContacts?.phone
          || '',
        spouseEmail:
          clientData.spouseEmail
          || spouseFromContacts?.email
          || '',
        // Legacy records only have the date-only JSON value; treat it as the
        // interview timestamp so the field is never blank for an old client.
        // Anchored to local midnight; a bare "2024-03-05" parses as UTC midnight
        // and reads back as the previous evening west of Greenwich.
        intakeAt: clientData.intakeAt
          || (intakeProfile.intakeDate
            ? fromDatetimeLocalValue(`${String(intakeProfile.intakeDate).slice(0, 10)}T00:00`)
            : null),
        intakeByUserId: clientData.intakeByUserId || clientData.intakeBy?.id || null,
        intakeProfile: {
          ...intakeProfile,
          intakeDate: intakeProfile.intakeDate
            ? String(intakeProfile.intakeDate).split('T')[0]
            : '',
        },
      });
      if (clientData.contacts?.length) {
        setContacts(clientData.contacts);
      } else if (clientData.id) {
        loadContacts(clientData.id);
      } else {
        setContacts([]);
      }
    } else {
      const now = new Date();
      setFormData({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        address: '',
        mailingAddress: '',
        city: '',
        state: '',
        county: '',
        zipCode: '',
        preferredName: '',
        dateOfBirth: null,
        clientType: 'individual',
        status: 'active',
        maritalStatus: '',
        spouseName: '',
        spousePhone: '',
        spouseEmail: '',
        employmentStatus: '',
        employerName: '',
        jobTitle: '',
        preferredContactMethod: 'email',
        preferredLanguage: 'English',
        interpreterRequired: false,
        safeToLeaveVoicemail: true,
        entityLegalName: '',
        entityDba: '',
        entityJurisdiction: '',
        entityFormationDate: null,
        businessPurpose: '',
        referralSource: '',
        referralDetails: '',
        notes: '',
        // Stamped the moment the intake form is opened; the interviewer defaults
        // to the signed-in staff member and both stay editable.
        intakeAt: now.toISOString(),
        intakeByUserId: currentUserIsIntakeStaff ? currentUser?.id || null : null,
        intakeProfile: { intakeDate: todayDateInputValue() },
      });
      setContacts([]);
    }
    setDraftContact(emptyContact());
  }, [clientData, mode, open, loadContacts]);

  // When contacts load async, hydrate spouse phone/email if still empty
  useEffect(() => {
    if (!contacts.length) return;
    const spouse = contacts.find((c) => c.contactType === 'spouse' && c.isPrimary)
      || contacts.find((c) => c.contactType === 'spouse');
    if (!spouse) return;
    setFormData((prev) => ({
      ...prev,
      spousePhone: prev.spousePhone || spouse.phone || '',
      spouseEmail: prev.spouseEmail || spouse.email || '',
      spouseName: prev.spouseName || [spouse.firstName, spouse.lastName].filter(Boolean).join(' ').trim(),
    }));
  }, [contacts]);

  const handleInputChange = (field: string, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  /**
   * The intake timestamp is the source of truth; `intakeProfile.intakeDate` is
   * kept in step because firm templates merge {{Date_of_Intake}} from it.
   */
  const handleIntakeAtChange = (localValue: string) => {
    const iso = fromDatetimeLocalValue(localValue);
    setFormData((prev) => ({
      ...prev,
      intakeAt: iso,
      // Taken from the picker's own local text, not the ISO instant: an 8pm
      // intake is still today here but tomorrow in UTC, and this is the value
      // firm templates merge {{Date_of_Intake}} from.
      intakeProfile: {
        ...(prev.intakeProfile || {}),
        intakeDate: localValue ? localValue.slice(0, 10) : '',
      },
    }));
  };

  const handleSubmit = async () => {
    if (!formData.firstName || !formData.lastName || !formData.email) {
      toast.error('Please fill in all required fields');
      return;
    }

    setIsSubmitting(true);
    try {
      const profile = { ...(formData.intakeProfile || {}) } as Record<string, unknown>;
      if (profile.intakeDate) {
        profile.intakeDate = String(profile.intakeDate).split('T')[0];
      } else {
        delete profile.intakeDate;
      }
      const submitData: Partial<Client> = {
        ...formData,
        intakeProfile: profile,
        spousePhone: formData.spousePhone || '',
        spouseEmail: formData.spouseEmail || '',
        intakeAt: formData.intakeAt || null,
        intakeByUserId: formData.intakeByUserId || null,
        ...(formData.dateOfBirth && {
          dateOfBirth: toDateOnly(formData.dateOfBirth),
        }),
      };
      // Read-only association; sending it back would be ignored at best.
      delete (submitData as Record<string, unknown>).intakeBy;
      await onSave(submitData);
      toast.success(mode === 'create' ? 'Client created successfully!' : 'Client updated successfully!');
      onClose();
    } catch (error) {
      toast.error('Failed to save client');
      console.error('Error saving client:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const persistContact = async () => {
    if (!clientData?.id) {
      toast.error('Save the client first, then add related people');
      return;
    }
    if (!draftContact.firstName && !draftContact.lastName && !draftContact.organizationName) {
      toast.error('Enter a name for this contact');
      return;
    }
    const token = getAuthToken();
    const res = await fetch(`/api/clients/${clientData.id}/contacts`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(draftContact),
    });
    if (!res.ok) {
      toast.error('Failed to add contact');
      return;
    }
    toast.success('Contact added');
    setDraftContact(emptyContact());
    loadContacts(clientData.id);
  };

  const removeContact = async (contactId?: string) => {
    if (!clientData?.id || !contactId) return;
    const token = getAuthToken();
    const res = await fetch(`/api/clients/${clientData.id}/contacts/${contactId}`, {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) {
      toast.error('Failed to remove contact');
      return;
    }
    loadContacts(clientData.id);
  };

  const formatDate = (dateString: string | null | undefined) => {
    if (!dateString) return 'Not provided';
    return new Date(dateString).toLocaleDateString();
  };

  const getClientInitials = (firstName: string, lastName: string) =>
    `${firstName?.charAt(0) || ''}${lastName?.charAt(0) || ''}`.toUpperCase();

  const field = (
    label: string,
    key: string,
    opts: { md?: number; multiline?: boolean; rows?: number; type?: string; required?: boolean } = {}
  ) => (
    <Grid item xs={12} md={opts.md ?? 6} key={key}>
      <TextField
        fullWidth
        label={label}
        type={opts.type || 'text'}
        value={(formData as any)[key] ?? ''}
        onChange={(e) => handleInputChange(key, e.target.value || (opts.type === 'date' ? null : ''))}
        required={opts.required}
        disabled={readOnly}
        multiline={opts.multiline}
        rows={opts.rows}
        InputLabelProps={opts.type === 'date' ? { shrink: true } : undefined}
      />
    </Grid>
  );

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">
            {mode === 'create' ? 'New Client Intake' : mode === 'edit' ? 'Edit Client Intake' : 'Client Profile'}
          </Typography>
          <IconButton onClick={onClose}>
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {mode === 'view' && (
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <Avatar sx={{ width: 56, height: 56, mr: 2, bgcolor: 'primary.main' }}>
              {getClientInitials(clientData?.firstName || '', clientData?.lastName || '')}
            </Avatar>
            <Box>
              <Typography variant="h6">
                {clientData?.firstName} {clientData?.lastName}
                {clientData?.preferredName ? ` (“${clientData.preferredName}”)` : ''}
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <Chip label={clientData?.status} size="small" color="success" />
                <Chip label={clientData?.clientType} size="small" variant="outlined" />
                {clientData?.maritalStatus && (
                  <Chip label={clientData.maritalStatus} size="small" variant="outlined" />
                )}
              </Box>
            </Box>
          </Box>
        )}

        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab label="Overview" />
          <Tab label="Household & Kin" />
          <Tab label="Business" disabled={!isBusiness && mode !== 'create'} />
          <Tab label="Related People" />
          <Tab label="Notes & Referral" />
          <Tab label="Calls" disabled={mode === 'create' || !clientData?.id} />
        </Tabs>

        {tab === 0 && (
          <Grid container spacing={2}>
            {mode === 'view' ? (
              <>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2"><Email sx={{ mr: 1, verticalAlign: 'middle' }} />Email</Typography>
                  <Typography variant="body2" paragraph>{clientData?.email}</Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2"><Phone sx={{ mr: 1, verticalAlign: 'middle' }} />Phone</Typography>
                  <Typography variant="body2" paragraph>{clientData?.phone || 'Not provided'}</Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle2"><LocationOn sx={{ mr: 1, verticalAlign: 'middle' }} />Residential address</Typography>
                  <Typography variant="body2" paragraph>{clientData?.address || 'Not provided'}</Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle2">Mailing address</Typography>
                  <Typography variant="body2" paragraph>{clientData?.mailingAddress || 'Same as residential / not set'}</Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2"><CalendarToday sx={{ mr: 1, verticalAlign: 'middle' }} />Date of birth</Typography>
                  <Typography variant="body2">{formatDate(clientData?.dateOfBirth)}</Typography>
                </Grid>
              </>
            ) : (
              <>
                {field('First name *', 'firstName', { required: true })}
                {field('Last name *', 'lastName', { required: true })}
                {field('Preferred name', 'preferredName')}
                {field('Email *', 'email', { required: true, type: 'email' })}
                {field('Phone', 'phone')}
                {field('Date of birth', 'dateOfBirth', { type: 'date' })}
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth disabled={readOnly}>
                    <InputLabel>Client type *</InputLabel>
                    <Select
                      value={formData.clientType || 'individual'}
                      label="Client type *"
                      onChange={(e) => handleInputChange('clientType', e.target.value)}
                    >
                      <MenuItem value="individual">Individual</MenuItem>
                      <MenuItem value="llc">LLC</MenuItem>
                      <MenuItem value="corporation">Corporation</MenuItem>
                      <MenuItem value="partnership">Partnership</MenuItem>
                      <MenuItem value="other">Other organization</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth disabled={readOnly}>
                    <InputLabel>Status *</InputLabel>
                    <Select
                      value={formData.status || 'active'}
                      label="Status *"
                      onChange={(e) => handleInputChange('status', e.target.value)}
                    >
                      <MenuItem value="active">Active</MenuItem>
                      <MenuItem value="prospect">Prospect</MenuItem>
                      <MenuItem value="inactive">Inactive</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                {field('Residential address', 'address', { md: 12, multiline: true, rows: 2 })}
                {field('City', 'city')}
                {field('State', 'state')}
                {field('ZIP code', 'zipCode')}
                {field('County', 'county')}
                {field('Mailing address (if different)', 'mailingAddress', { md: 12, multiline: true, rows: 2 })}
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Intake date and time"
                    type="datetime-local"
                    value={toDatetimeLocalValue(formData.intakeAt)}
                    onChange={(e) => handleIntakeAtChange(e.target.value)}
                    disabled={readOnly}
                    InputLabelProps={{ shrink: true }}
                    helperText={
                      mode === 'create'
                        ? 'Stamped automatically — adjust if the interview happened earlier'
                        : 'Merges into {{Date_of_Intake}} and {{Time_of_Intake}} in firm templates'
                    }
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth disabled={readOnly}>
                    <InputLabel>Intake conducted by</InputLabel>
                    <Select
                      value={formData.intakeByUserId || ''}
                      label="Intake conducted by"
                      onChange={(e) => handleInputChange('intakeByUserId', e.target.value || null)}
                    >
                      <MenuItem value="">
                        <em>Not recorded</em>
                      </MenuItem>
                      {/* A previous interviewer who has since left the firm is no
                          longer assignable, so keep them listed on their record. */}
                      {formData.intakeBy?.id
                        && !intakeStaff.some((staff) => staff.id === formData.intakeBy?.id) && (
                        <MenuItem value={formData.intakeBy.id}>
                          {[formData.intakeBy.firstName, formData.intakeBy.lastName]
                            .filter(Boolean).join(' ') || formData.intakeBy.email} (inactive)
                        </MenuItem>
                      )}
                      {intakeStaff.map((staff) => (
                        <MenuItem key={staff.id} value={staff.id}>
                          {[staff.firstName, staff.lastName].filter(Boolean).join(' ') || staff.email}
                          {staff.role ? ` — ${staff.role}` : ''}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth disabled={readOnly}>
                    <InputLabel>Preferred contact method</InputLabel>
                    <Select
                      value={formData.preferredContactMethod || ''}
                      label="Preferred contact method"
                      onChange={(e) => handleInputChange('preferredContactMethod', e.target.value)}
                    >
                      <MenuItem value="email">Email</MenuItem>
                      <MenuItem value="phone">Phone</MenuItem>
                      <MenuItem value="sms">SMS</MenuItem>
                      <MenuItem value="mail">Mail</MenuItem>
                      <MenuItem value="portal">Client portal</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={6}>
                  {/* freeSolo so the list is a real dropdown without discarding a
                      language a firm already typed or one we did not think of. */}
                  <Autocomplete
                    freeSolo
                    options={CLIENT_LANGUAGES as string[]}
                    value={formData.preferredLanguage || ''}
                    disabled={readOnly}
                    onChange={(_e, value) => handleInputChange('preferredLanguage', value || '')}
                    onInputChange={(_e, value, reason) => {
                      if (reason === 'input') handleInputChange('preferredLanguage', value);
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Preferred language"
                        helperText="Pick from the list or type another language"
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={Boolean(formData.interpreterRequired)}
                        onChange={(e) => handleInputChange('interpreterRequired', e.target.checked)}
                        disabled={readOnly}
                      />
                    }
                    label="Interpreter required"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={formData.safeToLeaveVoicemail !== false}
                        onChange={(e) => handleInputChange('safeToLeaveVoicemail', e.target.checked)}
                        disabled={readOnly}
                      />
                    }
                    label="Safe to leave voicemail"
                  />
                </Grid>
              </>
            )}
          </Grid>
        )}

        {tab === 1 && (
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Alert severity="info" sx={{ mb: 1 }}>
                Family and next-of-kin belong on the client file (reusable across matters).
                Use Related People to add children, emergency contacts, and beneficiaries.
              </Alert>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth disabled={readOnly}>
                <InputLabel>Marital status</InputLabel>
                <Select
                  value={formData.maritalStatus || ''}
                  label="Marital status"
                  onChange={(e) => handleInputChange('maritalStatus', e.target.value)}
                >
                  <MenuItem value="">Not specified</MenuItem>
                  <MenuItem value="single">Single</MenuItem>
                  <MenuItem value="married">Married</MenuItem>
                  <MenuItem value="separated">Separated</MenuItem>
                  <MenuItem value="divorced">Divorced</MenuItem>
                  <MenuItem value="widowed">Widowed</MenuItem>
                  <MenuItem value="domestic_partnership">Domestic partnership</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            {field('Spouse / partner name', 'spouseName')}
            {field('Spouse phone', 'spousePhone')}
            {field('Spouse email', 'spouseEmail', { type: 'email' })}
            <Grid item xs={12} md={6}>
              <FormControl fullWidth disabled={readOnly}>
                <InputLabel>Employment status</InputLabel>
                <Select
                  value={formData.employmentStatus || ''}
                  label="Employment status"
                  onChange={(e) => handleInputChange('employmentStatus', e.target.value)}
                >
                  <MenuItem value="">Not specified</MenuItem>
                  <MenuItem value="employed">Employed</MenuItem>
                  <MenuItem value="self_employed">Self-employed</MenuItem>
                  <MenuItem value="unemployed">Unemployed</MenuItem>
                  <MenuItem value="retired">Retired</MenuItem>
                  <MenuItem value="disabled">Disabled</MenuItem>
                  <MenuItem value="student">Student</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            {field('Employer', 'employerName')}
            {field('Occupation', 'jobTitle')}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Number of children"
                value={String((formData.intakeProfile as Record<string, unknown>)?.numberOfChildren || '')}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    intakeProfile: {
                      ...(prev.intakeProfile || {}),
                      numberOfChildren: e.target.value,
                    },
                  }))
                }
                disabled={readOnly}
                helperText="Fills {{Number_of_Children}} in will / estate templates"
              />
            </Grid>
            <Grid item xs={12}>
              <Typography variant="body2" color="text.secondary">
                Occupation fills {'{{Client_Occupation}}'} in firm templates. Spouse phone/email sync to Related People (Spouse).
                City / State / County fill Testator and Client location shortcodes.
              </Typography>
            </Grid>
          </Grid>
        )}

        {tab === 2 && (
          <Grid container spacing={2}>
            {!isBusiness && (
              <Grid item xs={12}>
                <Alert severity="warning">
                  Switch Client type to LLC, Corporation, Partnership, or Other to capture entity intake.
                </Alert>
              </Grid>
            )}
            {field('Legal entity name', 'entityLegalName', { md: 12 })}
            {field('DBA / trade name', 'entityDba')}
            {field('Jurisdiction of formation', 'entityJurisdiction')}
            {field('Formation date', 'entityFormationDate', { type: 'date' })}
            {field('Nature of business', 'businessPurpose', { md: 12, multiline: true, rows: 3 })}
            <Grid item xs={12}>
              <Typography variant="body2" color="text.secondary">
                Add officers, owners, and authorized signatories under Related People.
              </Typography>
            </Grid>
          </Grid>
        )}

        {tab === 3 && (
          <Box>
            {mode === 'create' && (
              <Alert severity="info" sx={{ mb: 2 }}>
                Save the client first, then return here to add next of kin, executors, trustees, guardians, agents, witnesses, and beneficiaries used by will / estate templates.
              </Alert>
            )}
            {!readOnly && mode !== 'create' && (
              <Alert severity="info" sx={{ mb: 2 }}>
                Estate / will shortcodes: add contacts as Executor, Alternate executor, Trustee, Guardian, Primary/Successor agent, Witness, or Beneficiary (mark Primary when there are two of the same type).
              </Alert>
            )}
            {contactsLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
            ) : (
              <Paper variant="outlined" sx={{ mb: 2, overflow: 'auto' }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Type</TableCell>
                      <TableCell>Name</TableCell>
                      <TableCell>Relationship / role</TableCell>
                      <TableCell>Phone</TableCell>
                      <TableCell>Discuss?</TableCell>
                      {!readOnly && <TableCell align="right"> </TableCell>}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {contacts.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={6}>
                          <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                            No related people yet — add next of kin, children, or business officers.
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                    {contacts.map((c) => (
                      <TableRow key={c.id}>
                        <TableCell>{CONTACT_TYPE_LABELS[c.contactType] || c.contactType}</TableCell>
                        <TableCell>
                          {[c.firstName, c.lastName].filter(Boolean).join(' ') || c.organizationName || '—'}
                          {c.isPrimary ? ' ★' : ''}
                        </TableCell>
                        <TableCell>{c.relationship || c.roleTitle || '—'}</TableCell>
                        <TableCell>{c.phone || '—'}</TableCell>
                        <TableCell>{c.authorizedToDiscuss ? 'Yes' : 'No'}</TableCell>
                        {!readOnly && (
                          <TableCell align="right">
                            <IconButton size="small" onClick={() => removeContact(c.id)} aria-label="Remove">
                              <Delete fontSize="small" />
                            </IconButton>
                          </TableCell>
                        )}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Paper>
            )}

            {!readOnly && mode !== 'create' && (
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Typography variant="subtitle1" gutterBottom>Add related person</Typography>
                </Grid>
                <Grid item xs={12} md={4}>
                  <FormControl fullWidth>
                    <InputLabel>Contact type</InputLabel>
                    <Select
                      label="Contact type"
                      value={draftContact.contactType}
                      onChange={(e) => setDraftContact((p) => ({ ...p, contactType: e.target.value }))}
                    >
                      {Object.entries(CONTACT_TYPE_LABELS).map(([value, label]) => (
                        <MenuItem key={value} value={value}>{label}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="First name"
                    value={draftContact.firstName || ''}
                    onChange={(e) => setDraftContact((p) => ({ ...p, firstName: e.target.value }))}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="Last name"
                    value={draftContact.lastName || ''}
                    onChange={(e) => setDraftContact((p) => ({ ...p, lastName: e.target.value }))}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="Relationship / role"
                    value={draftContact.relationship || ''}
                    onChange={(e) => setDraftContact((p) => ({ ...p, relationship: e.target.value }))}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="Phone"
                    value={draftContact.phone || ''}
                    onChange={(e) => setDraftContact((p) => ({ ...p, phone: e.target.value }))}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="Email"
                    value={draftContact.email || ''}
                    onChange={(e) => setDraftContact((p) => ({ ...p, email: e.target.value }))}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="Title (officers)"
                    value={draftContact.roleTitle || ''}
                    onChange={(e) => setDraftContact((p) => ({ ...p, roleTitle: e.target.value }))}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Address"
                    value={draftContact.address || ''}
                    onChange={(e) => setDraftContact((p) => ({ ...p, address: e.target.value }))}
                    multiline
                    rows={2}
                    helperText="Used for Executor_Address, Guardian_Address, Witness addresses, etc."
                  />
                </Grid>
                <Grid item xs={12} md={8}>
                  <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center', height: '100%' }}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={Boolean(draftContact.isPrimary)}
                          onChange={(e) => setDraftContact((p) => ({ ...p, isPrimary: e.target.checked }))}
                        />
                      }
                      label="Primary"
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={Boolean(draftContact.authorizedToDiscuss)}
                          onChange={(e) => setDraftContact((p) => ({ ...p, authorizedToDiscuss: e.target.checked }))}
                        />
                      }
                      label="Authorized to discuss matter"
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={Boolean(draftContact.isDependent)}
                          onChange={(e) => setDraftContact((p) => ({ ...p, isDependent: e.target.checked }))}
                        />
                      }
                      label="Dependent"
                    />
                  </Box>
                </Grid>
                <Grid item xs={12}>
                  <Button variant="outlined" startIcon={<Add />} onClick={persistContact}>
                    Add contact
                  </Button>
                </Grid>
              </Grid>
            )}
          </Box>
        )}

        {tab === 4 && (
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth disabled={readOnly}>
                <InputLabel>Referral source</InputLabel>
                <Select
                  value={formData.referralSource || ''}
                  label="Referral source"
                  onChange={(e) => handleInputChange('referralSource', e.target.value)}
                >
                  <MenuItem value="">Not specified</MenuItem>
                  <MenuItem value="website">Website</MenuItem>
                  <MenuItem value="referral">Referral</MenuItem>
                  <MenuItem value="google_ads">Google Ads</MenuItem>
                  <MenuItem value="social_media">Social media</MenuItem>
                  <MenuItem value="cold_call">Cold call</MenuItem>
                  <MenuItem value="walk_in">Walk-in</MenuItem>
                  <MenuItem value="other">Other</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            {field('Referral details', 'referralDetails')}
            <Grid item xs={12}>
              <Typography variant="caption" color="text.secondary">
                Referral source/details fill {'{{Referred_By}}'} in firm templates. Notes fill {'{{Additional_Notes}}'}.
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Notes"
                value={formData.notes || ''}
                onChange={(e) => handleInputChange('notes', e.target.value)}
                multiline
                rows={4}
                disabled={readOnly}
              />
            </Grid>
            {mode === 'view' && (
              <Grid item xs={12}>
                <Divider sx={{ my: 1 }} />
                <Typography variant="caption" color="text.secondary" display="block">
                  <Notes sx={{ fontSize: 14, mr: 0.5, verticalAlign: 'middle' }} />
                  Created: {formatDate(clientData?.createdAt)} · Updated: {formatDate(clientData?.updatedAt)}
                </Typography>
              </Grid>
            )}
          </Grid>
        )}

        {tab === 5 && clientData?.id && (
          <CallLogPanel clientId={clientData.id} />
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>{mode === 'view' ? 'Close' : 'Cancel'}</Button>
        {mode === 'view' && (
          <Button variant="contained" startIcon={<Edit />} onClick={() => onModeChange?.('edit')}>
            Edit intake
          </Button>
        )}
        {(mode === 'edit' || mode === 'create') && (
          <Button
            variant="contained"
            startIcon={isSubmitting ? <CircularProgress size={20} /> : <Save />}
            onClick={handleSubmit}
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Saving…' : 'Save client'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default ClientDialog;
